// AGC: Auth store — Zustand-based auth state management (BoviSense pattern)
// AGC: Module-level onAuthStateChanged listener keeps store in sync with Firebase
// AGC: Offline-first: profile cached in localStorage for instant app start
import { create } from 'zustand'
import { User, ConfirmationResult } from 'firebase/auth'
import { auth } from '../lib/firebase'
import {
  onAuthStateChange,
  initializeGoogleAuth,
  signInWithGoogle as firebaseSignInWithGoogle,
  signInWithApple as firebaseSignInWithApple,
  handleRedirectResult,
  signInWithEmail as firebaseSignInWithEmail,
  createUserWithEmail as firebaseCreateUserWithEmail,
  signOutUser,
  sendPasswordReset as firebaseSendPasswordReset,
  getUserProfile,
  createUserProfile,
  updateLastLogin,
  initRecaptchaVerifier,
  sendPhoneOTP,
  verifyPhoneOTP,
  getIdToken,
  getIdTokenOrRefresh,
} from './firebase-auth'
import type { AuthUser, UserProfile, LoginCredentials, RegisterData, UserRole } from '../types/user'
import { clearBiometricPreferences } from './biometricPreferences'
import { syncModelOnLogin } from './recommendationModelService'

// ============================================================
// AGC: Offline-first profile cache (localStorage)
// ============================================================

const getProfileCacheKey = (uid: string): string => `droidfarm_user_profile_${uid}`

const cacheUserProfile = (uid: string, profile: UserProfile): void => {
  try {
    localStorage.setItem(getProfileCacheKey(uid), JSON.stringify({ profile, cachedAt: Date.now() }))
  } catch { /* AGC: ignore storage errors */ }
}

const getCachedUserProfile = (uid: string): UserProfile | null => {
  try {
    const cached = localStorage.getItem(getProfileCacheKey(uid))
    if (cached) return JSON.parse(cached).profile
  } catch { /* AGC: ignore parse errors */ }
  return null
}

// ============================================================
// AGC: Error messages (Portuguese)
// ============================================================

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/email-already-in-use': 'Este email já está em uso',
  'auth/invalid-email': 'Email inválido',
  'auth/weak-password': 'Senha muito fraca. Use pelo menos 6 caracteres',
  'auth/user-disabled': 'Esta conta foi desativada',
  'auth/user-not-found': 'Usuário não encontrado',
  'auth/wrong-password': 'Senha incorreta',
  'auth/invalid-credential': 'Credenciais inválidas',
  'auth/too-many-requests': 'Muitas tentativas. Tente novamente mais tarde',
  'auth/popup-closed-by-user': 'Login cancelado',
  'auth/network-request-failed': 'Erro de conexão. Verifique sua internet',
  'auth/invalid-phone-number': 'Número de telefone inválido',
  'auth/invalid-verification-code': 'Código de verificação inválido',
  'auth/code-expired': 'Código expirado. Solicite um novo',
}

const getAuthErrorMessage = (code: string): string =>
  AUTH_ERROR_MESSAGES[code] || 'Erro de autenticação. Tente novamente'

// ============================================================
// AGC: Zustand store
// ============================================================

interface AuthStoreState {
  user: AuthUser | null
  isLoading: boolean
  error: string | null
  isFirstLogin: boolean
  confirmationResult: ConfirmationResult | null

  // AGC: Actions
  loginWithGoogle: () => Promise<void>
  loginWithApple: () => Promise<void>
  loginWithEmail: (credentials: LoginCredentials) => Promise<void>
  loginWithPhone: (phoneNumber: string, containerId: string) => Promise<void>
  verifyOTP: (otp: string) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  clearError: () => void
  setFirstLoginComplete: () => void
  getAuthToken: () => Promise<string | null>
  refreshProfile: () => Promise<void>

  // AGC: Internal
  _setUser: (user: AuthUser | null) => void
  _setLoading: (loading: boolean) => void
  _setError: (error: string | null) => void
}

export const useAuthStore = create<AuthStoreState>((set, get) => ({
  user: null,
  isLoading: true,
  error: null,
  isFirstLogin: false,
  confirmationResult: null,

  loginWithGoogle: async () => {
    set({ isLoading: true, error: null })
    try {
      await firebaseSignInWithGoogle()
      // AGC: onAuthStateChanged listener handles the rest
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code || ''
      set({ error: getAuthErrorMessage(code), isLoading: false })
    }
  },

  loginWithApple: async () => {
    set({ isLoading: true, error: null })
    try {
      await firebaseSignInWithApple()
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code || ''
      set({ error: getAuthErrorMessage(code), isLoading: false })
    }
  },

  loginWithEmail: async (credentials) => {
    set({ isLoading: true, error: null })
    try {
      await firebaseSignInWithEmail(credentials.email, credentials.password)
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code || ''
      set({ error: getAuthErrorMessage(code), isLoading: false })
    }
  },

  loginWithPhone: async (phoneNumber, containerId) => {
    set({ isLoading: true, error: null })
    try {
      const recaptchaVerifier = initRecaptchaVerifier(containerId)
      const result = await sendPhoneOTP(phoneNumber, recaptchaVerifier)
      set({ confirmationResult: result, isLoading: false })
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code || ''
      set({ error: getAuthErrorMessage(code), isLoading: false })
    }
  },

  verifyOTP: async (otp) => {
    const { confirmationResult } = get()
    if (!confirmationResult) {
      set({ error: 'Código OTP expirado. Tente novamente.' })
      return
    }
    set({ isLoading: true, error: null })
    try {
      await verifyPhoneOTP(confirmationResult, otp)
      set({ confirmationResult: null })
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code || ''
      set({ error: getAuthErrorMessage(code), isLoading: false })
    }
  },

  register: async (data) => {
    set({ isLoading: true, error: null })
    try {
      const credential = await firebaseCreateUserWithEmail(data.email, data.password, data.displayName)
      if (credential.user) {
        await createUserProfile(credential.user, {
          displayName: data.displayName,
          phoneNumber: data.phoneNumber || null,
          role: 'user' as UserRole,
        })
        set({ isFirstLogin: true })
      }
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code || ''
      set({ error: getAuthErrorMessage(code), isLoading: false })
    }
  },

  logout: async () => {
    set({ isLoading: true })
    try {
      clearBiometricPreferences()
      await signOutUser()
      set({ user: null, isFirstLogin: false, error: null, isLoading: false })
    } catch {
      set({ error: 'Erro ao sair', isLoading: false })
    }
  },

  resetPassword: async (email) => {
    set({ isLoading: true, error: null })
    try {
      await firebaseSendPasswordReset(email)
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code || ''
      set({ error: getAuthErrorMessage(code) })
    } finally {
      set({ isLoading: false })
    }
  },

  clearError: () => set({ error: null }),
  setFirstLoginComplete: () => set({ isFirstLogin: false }),
  // AGC: v3.9.5 — auto-refresh on null cached token (sub-user idle race fix)
  getAuthToken: async () => getIdTokenOrRefresh(),

  // AGC: Re-fetch the current user's profile from Firestore and update store + cache.
  // AGC: Used after flows that mutate the user doc server-side (e.g. invite accept writes farmRole).
  refreshProfile: async () => {
    const current = get().user
    if (!current?.uid) return
    try {
      const fresh = await getUserProfile(current.uid)
      if (!fresh) return
      cacheUserProfile(current.uid, fresh)
      set({
        user: {
          uid: current.uid,
          email: current.email,
          displayName: current.displayName,
          photoURL: current.photoURL,
          phoneNumber: current.phoneNumber,
          emailVerified: current.emailVerified,
          profile: fresh,
        },
      })
    } catch {
      // AGC: Non-fatal — caller can proceed; next auth event will reconcile
    }
  },

  _setUser: (user) => set({ user }),
  _setLoading: (loading) => set({ isLoading: loading }),
  _setError: (error) => set({ error }),
}))

// ============================================================
// AGC: Module-level auth state listener (fires outside React)
// ============================================================

const AUTH_TIMEOUT_MS = 8000

// AGC: Initialize Google Auth plugin (non-blocking)
initializeGoogleAuth().catch(() => {})
handleRedirectResult().catch(() => {})

// AGC: Timeout fallback — show login screen if auth never resolves
const authTimeout = window.setTimeout(() => {
  if (useAuthStore.getState().isLoading) {
    console.warn('AGC: Auth state timed out after', AUTH_TIMEOUT_MS, 'ms')
    useAuthStore.setState({ isLoading: false })
  }
}, AUTH_TIMEOUT_MS)

// AGC: Auth state change listener — handles sign-in, sign-out, and profile sync
onAuthStateChange(async (firebaseUser: User | null) => {
  window.clearTimeout(authTimeout)

  if (!firebaseUser) {
    useAuthStore.setState({ user: null, isFirstLogin: false, isLoading: false })
    return
  }

  try {
    // AGC: Build AuthUser from Firebase User
    const buildAuthUser = (profile: UserProfile): AuthUser => ({
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      phoneNumber: firebaseUser.phoneNumber,
      emailVerified: firebaseUser.emailVerified,
      profile,
    })

    // AGC: OFFLINE-FIRST — check localStorage cache first
    const cachedProfile = getCachedUserProfile(firebaseUser.uid)

    if (cachedProfile) {
      // AGC: Check if deactivated
      if (!cachedProfile.isActive) {
        await signOutUser()
        useAuthStore.setState({
          user: null,
          error: 'Sua conta foi desativada. Entre em contato com o administrador.',
          isLoading: false,
        })
        return
      }

      // AGC: Show cached profile instantly
      useAuthStore.setState({
        user: buildAuthUser(cachedProfile),
        error: null,
        isLoading: false,
        isFirstLogin: !cachedProfile.biometricEnabled,
      })

      // AGC: Background sync if online
      if (navigator.onLine) {
        getUserProfile(firebaseUser.uid).then((fresh) => {
          if (fresh) {
            cacheUserProfile(firebaseUser.uid, fresh)
            useAuthStore.setState({ user: buildAuthUser(fresh) })
            updateLastLogin(firebaseUser.uid).catch(() => {})
          }
        }).catch(() => {})
        // AGC: Sync recommendation model to localStorage for offline scoring
        syncModelOnLogin().catch(() => {})
      }
      return
    }

    // AGC: No cache — need network
    if (!navigator.onLine) {
      useAuthStore.setState({
        error: 'Você está offline. Conecte-se à internet para carregar seu perfil.',
        isLoading: false,
      })
      return
    }

    // AGC: Fetch or create profile from Firestore
    let profile = await getUserProfile(firebaseUser.uid)
    let firstLogin = false

    if (!profile) {
      profile = await createUserProfile(firebaseUser)
      firstLogin = true
    } else {
      await updateLastLogin(firebaseUser.uid)
      if (!profile.biometricEnabled) firstLogin = true
    }

    cacheUserProfile(firebaseUser.uid, profile)

    if (!profile.isActive) {
      await signOutUser()
      useAuthStore.setState({
        user: null,
        error: 'Sua conta foi desativada. Entre em contato com o administrador.',
        isLoading: false,
      })
      return
    }

    useAuthStore.setState({
      user: buildAuthUser(profile),
      error: null,
      isLoading: false,
      isFirstLogin: firstLogin,
    })
    // AGC: Sync recommendation model to localStorage after fresh login
    syncModelOnLogin().catch(() => {})
  } catch {
    // AGC: On error, fall back to cache
    const cached = getCachedUserProfile(firebaseUser.uid)
    if (cached) {
      useAuthStore.setState({
        user: {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          phoneNumber: firebaseUser.phoneNumber,
          emailVerified: firebaseUser.emailVerified,
          profile: cached,
        },
        error: null,
        isLoading: false,
      })
    } else {
      useAuthStore.setState({
        user: null,
        error: 'Erro ao carregar perfil do usuário',
        isLoading: false,
      })
    }
  }
})
